import HeroSection, { HeroContent } from '../../../components/feature/sections/HeroSection';
import TrustBar from '../../../components/feature/sections/TrustBar';
import ContentSection, { ContentSectionData } from '../../../components/feature/sections/ContentSection';
import AIAnswerBlock, { AIAnswerContent } from '../../../components/feature/sections/AIAnswerBlock';
import ProcessSection, { ProcessContent } from '../../../components/feature/sections/ProcessSection';
import WhyChooseUs from '../../../components/feature/sections/WhyChooseUs';
import ServiceAreas from '../../../components/feature/sections/ServiceAreas';
import Testimonials from '../../../components/feature/sections/Testimonials';
import FAQSection, { FAQContent } from '../../../components/feature/sections/FAQSection';
import FinalCTA from '../../../components/feature/sections/FinalCTA';

const heroContent: HeroContent = {
  label: 'Hardscaping Services — Hayden, ID',
  headline: 'Custom Hardscaping Built for',
  headlineAccent: 'Idaho Conditions',
  subheadline:
    'Patios, walkways, retaining walls, and outdoor structures designed with premium materials and built to withstand North Idaho seasons — for decades of lasting beauty.',
  primaryCTA: { label: 'Get Free Estimate', href: '/#estimate' },
  secondaryCTA: { label: '(208) 659-6067', href: 'tel:+12086596067' },
  backgroundImage: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/12/Hardscaping.jpg',
  stats: [
    { value: '500+', label: 'Projects Delivered' },
    { value: '10+', label: 'Years Experience' },
    { value: '5.0★', label: 'Google Rated' },
  ],
};

const servicesContent: ContentSectionData = {
  badge: 'Hardscaping Services',
  heading: 'What We Build',
  headingAccent: 'In Hardscape & Stone',
  subheading:
    'From patios to retaining walls — durable, stunning outdoor structures designed to add value and usability to your property.',
  items: [
    {
      title: 'Patio Installation',
      desc: "Custom concrete, paver, and flagstone patios designed for North Idaho's climate — built to handle freeze-thaw cycles and heavy use.",
      image: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/12/Hardscaping.jpg',
      icon: 'ri-layout-masonry-line',
      href: '/#estimate',
    },
    {
      title: 'Retaining Walls',
      desc: 'Structural and decorative retaining walls that control erosion, manage slope, and define outdoor spaces with clean, lasting construction.',
      image: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/12/Retaining-Wall.jpg',
      icon: 'ri-stack-line',
      href: '/#estimate',
    },
    {
      title: 'Walkways & Paths',
      desc: 'Concrete, paver, and natural stone walkways that connect your outdoor spaces, improve safety, and elevate your property\'s curb appeal.',
      image: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/12/Custom-Landscaping.jpg',
      icon: 'ri-footprint-line',
      href: '/#estimate',
    },
    {
      title: 'French Drains & Drainage',
      desc: 'Professional drainage systems integrated into hardscape design to protect your foundation and keep your outdoor areas functional year-round.',
      image: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/12/French-Drains.jpg',
      icon: 'ri-drop-line',
      href: '/#estimate',
    },
    {
      title: 'Outdoor Living Spaces',
      desc: 'Custom outdoor areas combining hardscape, landscape, and structural elements — fire pits, seating zones, and entertainment spaces.',
      image: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/12/Custom-Landscaping.jpg',
      icon: 'ri-fire-line',
      href: '/#estimate',
    },
    {
      title: 'Custom Stonework',
      desc: 'Decorative stonework, edging, garden beds, and feature elements that bring texture, character, and definition to any landscape design.',
      image: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/12/Turf-Installation.jpg',
      icon: 'ri-shape-2-line',
      href: '/#estimate',
    },
  ],
  ctaLabel: 'Get Your Free Hardscaping Estimate',
  ctaHref: '/#estimate',
};

const aiContent: AIAnswerContent = {
  badge: 'AI & Voice Search',
  heading: 'Hardscaping in Hayden, ID',
  subheading: 'Clear answers optimized for Google, AI search, and voice assistants.',
  answers: [
    {
      question: 'What is hardscaping?',
      answer:
        "Hardscaping refers to the non-living, structural elements of landscape design — including patios, walkways, retaining walls, outdoor kitchens, and drainage systems. Hardscaping adds usable space, improves drainage, increases property value, and creates visual structure in your yard.",
    },
    {
      question: 'Who installs patios and hardscaping near Hayden, ID?',
      answer:
        "CQC Landscaping installs patios, retaining walls, walkways, and custom hardscape features throughout Hayden, Post Falls, Coeur d'Alene, Rathdrum, Spokane WA, and surrounding North Idaho communities. We offer free on-site estimates.",
    },
    {
      question: 'How much does hardscaping cost?',
      answer:
        'Hardscaping costs vary by project size, materials, and complexity. Basic paver patios may start at several thousand dollars, while full outdoor living areas with walls and drainage can be significantly more. CQC provides free estimates after a site consultation.',
    },
    {
      question: 'How long does hardscaping take?',
      answer:
        'Most hardscaping projects take between 3 days and 2 weeks depending on scope, materials, and weather. A standard patio installation typically takes 3–5 days. Larger projects with retaining walls, drainage, and multiple elements may take 1–3 weeks.',
    },
  ],
};

const processContent: ProcessContent = {
  badge: 'Our Hardscaping Process',
  heading: 'From Concept to Completion',
  subheading: "Here's exactly how your hardscaping project gets built — step by step.",
  steps: [
    { number: '1', title: 'Site Consultation', desc: "We visit your property, assess the terrain, drainage, and existing features, and discuss your goals.", icon: 'ri-map-search-line' },
    { number: '2', title: 'Design & Layout', desc: 'We create a detailed design showing your patio, walls, or walkways with dimensions, materials, and drainage plan.', icon: 'ri-draft-line' },
    { number: '3', title: 'Material Selection', desc: "Choose from pavers, flagstone, natural rock, concrete, and more — materials selected for beauty and Idaho durability.", icon: 'ri-stack-line' },
    { number: '4', title: 'Excavation & Base', desc: 'Proper excavation, compaction, and base preparation ensure your hardscape holds up for decades.', icon: 'ri-tools-line' },
    { number: '5', title: 'Install & Seal', desc: 'Professional installation, finishing, and optional sealing with a final walkthrough to confirm your satisfaction.', icon: 'ri-checkbox-circle-line' },
  ],
};

const faqContent: FAQContent = {
  badge: 'Hardscaping FAQ',
  heading: 'Hardscaping Questions Answered',
  items: [
    {
      q: 'Who installs patios near me in Hayden or Post Falls, ID?',
      a: "CQC Landscaping installs patios and hardscaping features throughout Hayden, Post Falls, Coeur d'Alene, Rathdrum, Spokane WA, and surrounding areas. We offer custom designs tailored to each property and provide free on-site estimates.",
    },
    {
      q: 'How much does a patio cost in North Idaho?',
      a: "Patio costs depend on size, material (pavers, flagstone, concrete), and complexity. Basic patios may start at a few thousand dollars, while larger custom builds with fire features or drainage integrations can be significantly more. CQC provides detailed, free estimates.",
    },
    {
      q: 'How long does hardscaping take in Hayden, ID?',
      a: 'Most hardscaping projects in North Idaho take between 3 days and 2 weeks. A standard patio is typically 3–5 days. Larger projects with retaining walls, grading, and drainage systems may take 1–3 weeks.',
    },
    {
      q: 'Why is a proper base important for hardscaping?',
      a: "In North Idaho, freeze-thaw cycles can damage improperly installed hardscape. CQC always excavates to proper depth, compacts the base, and installs drainage where needed — ensuring your patio or walkway doesn't shift, sink, or crack over time.",
    },
    {
      q: 'Do you build retaining walls in North Idaho?',
      a: "Yes. CQC Landscaping builds structural and decorative retaining walls throughout North Idaho using concrete block, natural stone, and timber materials. We assess slope, soil, and drainage conditions before every wall installation.",
    },
  ],
};

export default function HardscapingPage() {
  return (
    <main>
      <HeroSection content={heroContent} />
      <TrustBar />
      <ContentSection content={servicesContent} />
      <AIAnswerBlock content={aiContent} />
      <ProcessSection content={processContent} />
      <WhyChooseUs />
      <ServiceAreas />
      <Testimonials />
      <FAQSection content={faqContent} />
      <FinalCTA
        heading="Ready for Your Custom Hardscaping Project?"
        subheading="Contact CQC Landscaping for a free on-site estimate. We'll design the perfect hardscape for your property in North Idaho."
      />
    </main>
  );
}
